import { LandingPageSections } from '../LandingPage.types'
import meetUs from './meetUs'
import organizationSchema from './schemas/organizationSchema'
import webpageSchema from './schemas/editorialWebPageSchema'

const editorialPageSections: LandingPageSections[] = [
  {
    type: 'hero',
    id: '1',
    desktopWidth: 'full',
    orientation: 'overlay',
    headerText: 'Editorial Process',
    images: {
      desktop:
        'https://images.babylist.com/image/upload/v1692129789/Dynamic%20Landing%20Pages/Editorial_Process_Hero_D.png',
      mobile:
        'https://images.babylist.com/image/upload/v1692129787/Dynamic%20Landing%20Pages/Editorial_Process_Hero_M.png',
    },
    subHeaderText:
      'Pregnancy and parenthood are filled with big decisions. We value our role as a trusted source of accurate, unbiased information.',
  },
  {
    type: 'text',
    id: '2',
    desktopWidth: 'full',
    heading: 'Who We Are',
    body: "At Babylist, our core mission is to take care of growing families so they can be confident, connected, happy and healthy. Our vision is to support every baby and the people who love them—from celebrating every milestone and getting the right gear to helping families feel supported and connected and stay healthy and well.<br /><br />We also recognize that every parent—and every journey to parenthood—is unique. Whether you're trying to conceive, newly pregnant, adopting, going through surrogacy or taking a different path to build your family, Babylist is here to help.",
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '3',
    desktopWidth: 'full',
    heading: 'Babylist Content',
    body: "Trying to choose a big-ticket item like a car seat or stroller? Overwhelmed by essentials like bottles, bibs and burp cloths and wondering how you'll decide? Want to know if the symptom you're experiencing is normal, or what to expect from each week of pregnancy? Our trusted, expert-backed content has you covered.<br /><br /><a href='/hello-baby'>Babylist content</a> supports you in pregnancy and parenthood whether you're a first-timer or if you've been down this road before. Our articles offer practical advice, emphasize medical expertise and lead you through the ups and downs of pregnancy and what comes after.",
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '4',
    desktopWidth: 'full',
    heading: 'Babylist Editorial Policies',
    body: `<p>We take our editorial policies and processes very seriously at Babylist. Pregnancy and parenthood are filled with particularly significant, sensitive choices, from big decisions around things like birth and postpartum to smaller (but sometimes just as important) considerations like which stroller to add to your baby registry. We appreciate our part in these moments and value our role as a trusted source of accurate, unbiased information.</p>
    <p>Our editorial process centers around three main tenets: editorial independence, medically sound information and firsthand experience.</p>
    <ul>
    <li><strong>The Babylist content team has editorial independence.</strong> What does that mean for you as a reader and a consumer? It means we write articles and product guides without external influence. We are unbiased in our product recommendations and retailer and price point agnostic. And we don't show affinities toward any brand—no matter how many free car seats they send us.</li>
    <li><strong>We value medically sound, evidence-based information.</strong> Our content is guided by the principles of two main sources, the <a href="https://www.aap.org/">American Academy of Pediatrics</a> (AAP) and the <a href="https://www.cdc.gov/">Centers for Disease Control and Prevention</a> (CDC). The AAP is an organization of 67,000 pediatricians committed to the optimal physical, mental and social health and well-being of all infants and children. The CDC is the nation's leading science-based, data-driven service organization that protects the public's health. We use both sources for the most up-to-date information, from topics like prenatal care and delivery to developmental milestones and safe sleep practices.</li>
    <li><strong>We prioritize firsthand information.</strong> Many Babylist editors and writers are parents, too; if we don't like a product or have concerns about a particular item, we assume you'll feel the same. We personally test hundreds of products, from toys and games to strollers and baby carriers, to find the best—and the most developmentally appropriate—options for your baby and toddler and your family. We draw from years of experience writing and researching and from our backgrounds in the fields of media, early childhood education and publishing, and we analyze customer reviews from hundreds of Babylist parents. We also conduct an annual survey that polls thousands of real Babylist families across the country and asks them to share the baby products they love the most and why.</li>
    </ul>
    `,
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '5',
    desktopWidth: 'full',
    heading: 'Fact-Check and Medical Review Policy',
    body: `<p>Babylist offers medically vetted health content to guide you from pregnancy to postpartum and into your parenting journey. Our writers and editors speak with accredited experts in medicine, mental health, nutrition, fitness and more to bring you fact-based content.</p><p>We collaborate with <a href="https://www.babylist.com/health-advisory-board">Babylist's Health Advisory Board</a>, a group of doctors, researchers and other health professionals from the fields of obstetrics and gynecology, psychology, pediatrics, nutrition and more, to feature real medical guidance in our content. The Board reviews Babylist health content for medical accuracy and we draw regularly on their knowledge and expertise to help inform our decisions and shape the practical advice we offer for parents, whether we're answering questions about keeping baby safe or giving an overview of what to expect from a certain week of pregnancy.</p>`,
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '6',
    desktopWidth: 'full',
    heading: 'Why Trust Babylist',
    body: `<p>Since the early days of Babylist, we've been committed to making the often overwhelming task of building a baby registry a little bit easier for expecting parents. Our team of engineers knew there had to be a better way than trying to juggle multiple registries from different stores—so Babylist's universal registry was born.</p>
    <p>But pregnancy and parenthood are so much more than adding bibs and bottles to a baby registry. So as our Babylist team of employees grew, so did our areas of expertise. Since then we've written hundreds of product guides and reviewed thousands of baby products, from high chairs and jogging strollers to crib mattresses and pacifiers. We created the Babylist Shop, a curated collection of the best baby gear tested by our experts and real parents, and custom Try-It Kits that let you test out different brands of popular baby products to ensure you find exactly what your little one needs. We've walked thousands of expecting parents through pregnancy week by week, then stayed by their side with developmental tips as their infants grew into babies and then into toddlers. (And we didn't forget about friends, family and grandparents, either!) Most recently we've established Babylist Health, a way to streamline getting a free breast pump through insurance in one seamless online shopping experience, and an 18K square feet flagship showroom set to open in Los Angeles in the fall of 2023.</p>
   <p>We recognize there are many sources to choose from while navigating conceiving, pregnancy, postpartum and parenting. At Babylist, you can always trust our expertise and core editorial values.</p>
   <ul style="list-style-type: none;">
  <li>
    <img
      src="https://images.babylist.com/image/upload/v1691761151/hello-baby/Checkbox.png"
      alt="Checkbox"
      height="16"
      width="16"
    />
    Our editorial team makes all decisions without external influence.
  </li>
  <li>
    <img
      src="https://images.babylist.com/image/upload/v1691761151/hello-baby/Checkbox.png"
      alt="Checkbox"
      height="16"
      width="16"
    />
    We rely on leading industry experts like the American Academy of Pediatrics and the Centers for Disease Control and Prevention.
  </li>
  <li>
    <img
      src="https://images.babylist.com/image/upload/v1691761151/hello-baby/Checkbox.png"
      alt="Checkbox"
      height="16"
      width="16"
    />
    We vet all of our health content via experts, medical professionals and the Babylist Health Advisory Board.
  </li>
  <li>
    <img
      src="https://images.babylist.com/image/upload/v1691761151/hello-baby/Checkbox.png"
      alt="Checkbox"
      height="16"
      width="16"
    />
    We're in the trenches with you, too. Many Babylist editors and writers are parents and have years of experience writing and researching.
  </li>
  <li>
    <img
      src="https://images.babylist.com/image/upload/v1691761151/hello-baby/Checkbox.png"
      alt="Checkbox"
      height="16"
      width="16"
    />
    We're constantly in touch with real Babylist parents, whether it's through surveys, reading product reviews, emails or user panels, to seek feedback, answer questions and stay up-to-date on the latest and greatest in baby products.
  </li>
</ul>
    `,
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'divider',
    id: '3',
    variant: 'squiggle',
  },
  {
    type: 'text',
    id: '7',
    desktopWidth: 'full',
    heading: 'Contact Us',
    body: 'If you have questions or comments, please email us at <a href="mailto:support@babylist.com">support@babylist.com</a>  or contact us via the <a href="https://help.babylist.com/hc/en-us">Babylist Help Center</a>.',
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'divider',
    id: '3',
    variant: 'squiggle',
  },
  {
    type: 'frequently_asked_questions',
    id: '70',
    accordion: false,
    questions: [
      {
        question: 'How does Babylist make money?',
        answer:
          'Babylist makes money in a few different ways. We earn money through products sold in the Babylist Shop. We also earn compensation from affiliate links in our content and through brand-sponsored emails, social videos and content on our site.',
        id: '1',
      },
      {
        question:
          "What standards and ethics guide Babylist's editorial processes?",
        answer:
          'Our editorial process at Babylist centers around three main tenants: editorial independence, medically sound information and firsthand experience. You can read more about our standards and ethics in the Editorial Policies section of our site.',
        id: '2',
      },
      {
        question:
          'Does Babylist receive affiliate commissions on the products we recommend?',
        answer:
          "Yes, Babylist may earn compensation from affiliate links in our content or by selling products in the Babylist Shop, but this does not influence which products we choose to cover. Our primary goal is to share the products we've tried and loved, and we only recommended trusted retailers.",
        id: '3',
      },
      {
        question:
          'What types of backgrounds does the Babylist editorial team have? What makes them experts?',
        answer:
          'Many of our Babylist editors and writers are parents themselves and have years of experience writing and researching, coming from media outlets like Motherly, the SF Chronicle, the New York Times and the fields of early childhood education and publishing.',
        id: '4',
      },
      {
        question:
          'How does Babylist ensure that all health and wellness content across the site is medically accurate?',
        answer:
          "All of Babylist's health content is vetted by experts, medical professionals and the Babylist Health Advisory Board. We also collaborate with our Health Advisory Board to inform content decisions and host community-focused social events.",
        id: '5',
      },
      {
        question:
          "Does Babylist have any brand-sponsored content? How can you distinguish a branded article from the rest of Babylist's content?",
        answer:
          'Yes, Babylist features sponsored content on the site. Our free site, apps and email newsletters are made possible by our sponsors. We limit our sponsored content to relevant partners that offer products and services we believe in and use ourselves, and you\'ll always see "sponsored" somewhere within the article or guide so you can distinguish it from Babylist\'s organic content.',
        id: '6',
      },
      {
        question:
          'Does Babylist portray diverse perspectives across different social and demographic differences?',
        answer: `Babylist is committed to anti-racism and inclusivity. It's our mission to create a supportive and inclusive community for parents of all kinds, and equally important that we create a supportive and inclusive environment for Babylist employees.
            <p>Being actively anti-racist is a journey, not a destination. To us, being actively anti-racist means:</p>
            <ul>
              <li>We educate employees about and give them the words to challenge microaggressions because language is a powerful indicator of belonging.</li>
              <li>We create a safe place for people to investigate their own blindspots and racism that come from growing up in a racist society.</li>
              <li>We commit to challenging our own biases and those of people around us.</li>
              <li>We construct our company programs and processes to screen and correct for bias and unintended impact on underrepresented groups.</li>
              <li>We use our reach and our resources to support those who are doing anti-racist work that benefits our users and communities.</li>
            </ul>
            <p>We commit to dismantling systemic racism in the ways that it creeps into corporate structures:</p>
            <ul>
              <li>We interview manager candidates for diversity, equity and inclusion aptitude so everyone has a manager who supports their sense of belonging. If the candidate doesn't pass, they won't be hired.</li>
              <li>We articulate our anti-racist stance in our onboarding program so it's clear where we stand.</li>
              <li>We survey the team annually to make sure we understand inclusivity and belongingness at Babylist.</li>
              <li>We measure the efforts of our DEI programs so we know if they're working and can set objective goals to fix them if they're not.</li>
            </ul> `,
        id: '7',
      },
    ],
  },
  ...meetUs,
  {
    type: 'text',
    id: '9',
    desktopWidth: 'full',
    heading: 'Our Favorite Product Guides',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '1234',
    heading: 'Our Favorite Product Guides',
    slugs: [
      'best-bassinets',
      'best-convertible-car-seats',
      'best-high-chairs',
      'best-baby-bottles',
      'best-diaper-bags',
      'best-baby-monitor',
      'best-baby-carriers',
      'best-breast-pump',
    ],
  },
  {
    type: 'text',
    id: '9',
    desktopWidth: 'full',
    heading: 'Our Favorite Pregnancy Guides',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '5678',
    heading: 'Our Favorite Pregnancy Guides',
    slugs: [
      'how-to-take-babys-temperature',
      'implantation',
      'early-pregnancy-symptoms',
      'what-to-pack-in-your-hospital-bag',
      'tylenol-while-pregnant',
      'how-to-store-breast-milk',
      'free-breast-pump-through-health-insurance',
      'pregnancy-week-by-week',
    ],
  },
  {
    type: 'text',
    id: '9',
    desktopWidth: 'full',
    heading: 'Our Favorite Baby Guides',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '91011',
    heading: 'Our Favorite Baby Guides',
    slugs: [
      'best-toys-6-month-old-baby',
      'how-many-diapers-babys-first-year',
      'how-many-baby-clothes-do-i-need',
      'best-toys-for-one-year-olds',
      'when-to-stop-swaddling',
      'best-baby-detergent',
      'best-carriers-for-toddlers',
      'best-toddler-towers',
    ],
  },
  {
    type: 'spacer',
    id: 'spacer_section_1',
  },
  {
    type: 'schema',
    id: '22',
    schema: organizationSchema,
  },
  {
    type: 'schema',
    id: '222',
    schema: webpageSchema,
  },
]

export default editorialPageSections
